/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/Columns" />
import * as React from "react";
import { ISettingsViewService, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Pill, PillSize } from "VSSUI/Pill";
import { ITableColumn, SimpleTableCell, TableColumnLayout } from "VSSUI/Table";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { getPoliciesViewService } from "../Services/PoliciesViewService";

export interface IPolicyColumn<TPolicy extends IPolicy = IPolicy> extends ITableColumn<TPolicy> {
    sortFunction?(a: TPolicy, b: TPolicy): number;
}

export const enabledColumn: IPolicyColumn = {
    columnLayout: TableColumnLayout.singleLine,
    id: "enabled",
    name: Resources.Enabled,
    readonly: true,
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicy>, tableItem: IPolicy): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <EnabledToggle policy={tableItem} />
            </SimpleTableCell>
        );
    },
    sortFunction: (a, b) => booleanComparer(a.value, b.value),
    sortProps: { ariaLabelAscending: Resources.SortByEnabledAsc, ariaLabelDescending: Resources.SortByEnabledDesc },
    width: 116
};

const EnabledToggle = ({ policy }: { policy: IPolicy }) => {
    const { pageContext } = useComponentContext();
    return (
        <Toggle
            offText={Resources.ToggleOff}
            onText={Resources.ToggleOn}
            checked={policy.value}
            onChange={event => {
                event.preventDefault();
                getPoliciesViewService(pageContext).savePolicy({ ...policy, value: !policy.value });
            }}
            disabled={policy.isInherited || policy.isEnterpriseManaged || !!policy.disabled}
        />
    );
};

interface IPolicyWithPath extends IPolicy {
    complexSettings: {
        filenamePatterns: string;
    };
}

export const pathColumn: IPolicyColumn<IPolicyWithPath> = {
    columnLayout: TableColumnLayout.singleLine,
    id: "pathScope",
    name: Resources.PathFilter,
    readonly: true,
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicyWithPath>, tableItem: IPolicyWithPath): JSX.Element => {
        const cellValue = tableItem.complexSettings.filenamePatterns || "";
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <div className="flex-column text-ellipsis">
                    <div className="text-ellipsis">
                        <Tooltip text={cellValue}>
                            <span className="body-m">{cellValue}</span>
                        </Tooltip>
                    </div>
                </div>
            </SimpleTableCell>
        );
    },
    sortFunction: (a, b) => localeIgnoreCaseComparer(a.complexSettings.filenamePatterns, b.complexSettings.filenamePatterns),
    sortProps: { ariaLabelAscending: Resources.SortByFilenamePatternsAsc, ariaLabelDescending: Resources.SortByFilenamePatternsDesc },
    width: -40
};

export const inheritanceColumn: IPolicyColumn = {
    columnLayout: TableColumnLayout.singleLine,
    id: "inheritance",
    name: Resources.Inheritance,
    readonly: true,
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicy>, tableItem: IPolicy): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                {(tableItem.isInherited || tableItem.isEnterpriseManaged) && <InheritanceLink policy={tableItem} />}
            </SimpleTableCell>
        );
    },
    sortFunction: (a, b) => booleanComparer(Boolean(a.isInherited), Boolean(b.isInherited)),
    sortProps: { ariaLabelAscending: Resources.SortByInheritanceAsc, ariaLabelDescending: Resources.SortByInheritanceDesc },
    width: 120
};

const InheritanceLink = ({ policy }: { policy: IPolicy }) => {
    const { pageContext } = useComponentContext();
    const settingSvc = pageContext.getService<ISettingsViewService>("SettingsViewService");

    const cellValue = policy.isInherited ? Resources.Inheritance : undefined;
    const cellContent = (
        <span>
            <Icon className="settings-policies-icon icon-margin" iconName={"Info"} ariaHidden={"true"} />
            <span className="body-m">{cellValue}</span>
        </span>
    );

    return (
        <div className="flex-column text-ellipsis">
            <div className="text-ellipsis">
                <Tooltip text={Resources.InheritanceMessageTable}>
                    {!policy.isEnterpriseManaged ? (
                        <Link
                            onClick={event => {
                                if (!event.shiftKey && !event.ctrlKey) {
                                    const viewService = getPoliciesViewService(pageContext);
                                    viewService.goToRef(policy.refName!, policy.repositoryId);
                                    event.preventDefault();
                                }
                            }}
                            href={settingSvc.getUrl(
                                policy.repositoryId,
                                policy.refName,
                                policy.repositoryId ? SettingsTabIds.policies : SettingsTabIds.policiesMid
                            )}
                        >
                            {cellContent}
                        </Link>
                    ) : (
                        cellContent
                    )}
                </Tooltip>
            </div>
            {policy.isEnterpriseManaged && (
                <div>
                    <Tooltip text={Resources.ReadOnlyTooltip}>
                        <Pill size={PillSize.compact}>{Resources.ReadOnly}</Pill>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

function booleanComparer(a: boolean, b: boolean): number {
    return a === b ? 0 : a ? 1 : -1;
}
